import React, { useEffect, useState, useContext } from "react";
import { db } from "../../../firebaseConfig";
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from "firebase/firestore";
import styles from "./StudentTimetable.module.css";
import { AppContext } from "../../../Context/AppContext";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

export default function Timetable() {
    const { user } = useContext(AppContext);

    const [slots, setSlots] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [isCR, setIsCR] = useState(false);
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        if (user?.department && user?.program && user?.semester && user?.section) {
            checkIfCR();
            fetchTimetable();
        }
    }, [user]);

    const checkIfCR = async () => {
        try {
            const crRef = collection(db, "CR");
            const q = query(crRef, where("rollNumber", "==", user.stdRegNumber.trim()));
            const crSnap = await getDocs(q);
            if (!crSnap.empty) setIsCR(true);
        } catch (err) {
            console.error("Error checking CR:", err);
        }
    };

    const fetchTimetable = async () => {
        setLoading(true);
        setMessage("");
        setSlots([]);
        try {
            const docId = `${user.department}_${user.program}_${user.semester}_${user.section}`;
            const docRef = doc(db, "timetables", docId);
            const snapshot = await getDoc(docRef);
            if (snapshot.exists()) {
                const data = snapshot.data();
                if (data.slots?.length > 0) {
                    setSlots(data.slots);
                } else {
                    setMessage("No timetable data found for your class.");
                }
            } else {
                setMessage("No timetable found for your class.");
            }
        } catch (err) {
            console.error("Error fetching timetable:", err);
            setMessage("Failed to fetch timetable.");
        } finally {
            setLoading(false);
        }
    };

    const handleCellChange = (slotIndex, day, field, value) => {
        setSlots((prev) => {
            const copy = JSON.parse(JSON.stringify(prev));
            copy[slotIndex].days[day][field] = value;
            return copy;
        });
    };

    const handleSave = async () => {
        try {
            const docId = `${user.department}_${user.program}_${user.semester}_${user.section}`;
            const docRef = doc(db, "timetables", docId);
            await updateDoc(docRef, { slots });
            setEditMode(false);
            setMessage("Timetable updated successfully.");
        } catch (err) {
            console.error("Error saving timetable:", err);
            setMessage("Failed to save updates.");
        }
    };

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>My Timetable</h2>

            {loading && <p>Loading timetable...</p>}
            {message && <p className={styles.message}>{message}</p>}

            {slots.length > 0 && (
                <>
                    {/* Timetable */}
                    <div className={styles.tableWrapper}>
                        <table className={styles.timetable}>
                            <thead>
                            <tr>
                                <th>-</th>
                                <th>Slot</th>
                                {DAYS.map((day) => (
                                    <th key={day}>{day}</th>
                                ))}
                            </tr>
                            </thead>
                            <tbody>
                            {slots.map((slot, si) => (
                                <React.Fragment key={si}>
                                    {/* Subject Row */}
                                    <tr className={styles.rowLabel}>
                                        <td className={styles.rowType}>Subject</td>
                                        <td className={styles.slotLabel}>{slot.label}</td>
                                        {DAYS.map((d) => (
                                            <td key={d}>
                                                {editMode ? (
                                                    <input
                                                        className={styles.cellInput}
                                                        value={slot.days[d]?.subject || ""}
                                                        onChange={(e) =>
                                                            handleCellChange(si, d, "subject", e.target.value)
                                                        }
                                                    />
                                                ) : (
                                                    <span className={styles.readOnlyCell}>
                                                {slot.days[d]?.subject || "-"}
                                            </span>
                                                )}
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Room Row */}
                                    <tr>
                                        <td className={styles.rowType}>Room</td>
                                        <td className={styles.slotLabel}></td>
                                        {DAYS.map((d) => (
                                            <td key={d}>
                                                {editMode ? (
                                                    <input
                                                        className={styles.cellInput}
                                                        value={slot.days[d]?.room || ""}
                                                        onChange={(e) =>
                                                            handleCellChange(si, d, "room", e.target.value)
                                                        }
                                                    />
                                                ) : (
                                                    <span className={styles.readOnlyCell}>
                                                {slot.days[d]?.room || "-"}
                                            </span>
                                                )}
                                            </td>
                                        ))}
                                    </tr>

                                    {/* Teacher Row */}
                                    <tr>
                                        <td className={styles.rowType}>Teacher</td>
                                        <td className={styles.slotLabel}></td>
                                        {DAYS.map((d) => (
                                            <td key={d}>
                                                {editMode ? (
                                                    <input
                                                        className={styles.cellInput}
                                                        value={slot.days[d]?.teacher || ""}
                                                        onChange={(e) =>
                                                            handleCellChange(si, d, "teacher", e.target.value)
                                                        }
                                                    />
                                                ) : (
                                                    <span className={styles.readOnlyCell}>
                                                {slot.days[d]?.teacher || "-"}
                                            </span>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                </React.Fragment>
                            ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Controls - same position as Page 1 */}
                    {isCR && !editMode && (
                        <div className={styles.controls}>
                            <button
                                className={styles.addSlotBtn}
                                onClick={() => setEditMode(true)}
                            >
                                Update Timetable
                            </button>
                        </div>
                    )}
                    {isCR && editMode && (
                        <div className={styles.controls}>
                            <button
                                className={styles.saveBtn}
                                onClick={handleSave}
                                disabled={loading}
                            >
                                {loading ? "Saving..." : "Save Changes"}
                            </button>
                            <button
                                className={styles.addSlotBtn}
                                onClick={() => setEditMode(false)}
                            >
                                Cancel
                            </button>
                        </div>
                    )}
                </>
            )}

        </div>
    );
}
